import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

import { Db } from '../../services/Db';

import {
	Article,
	GET_ISSUE_BY_ID_QUERY,
	reduceExcludeComponents,
	clone,
	getAccessToken,
	getUserRole,
	Issue,
	getAppToken,
	mapIssuesNonImplementedFeatures,
	isNumeric,
} from '../../constants';
import config from '../../config';
import { useFetchPolicy } from '../../App.hooks';
import { useIssueNotFound } from '../../hooks/use-issue';

/**
 * React hook that load data from navigation
 *
 */
export function useLoad(): NavigationResponse {
	const { id, articleId } = useParams();
	const issueId = id;
	const [currentArticle, setCurrentArticle] = useState<any>(null);
	const { issue, isLoading, error } = useGetIssue(`${issueId}`);

	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!issue.articles) {
			return;
		}
		let art = issue.articles[0];
		for (const article of issue.articles) {
			if (article.id === articleId || article.slug === articleId) {
				art = article;
				break;
			}
		}

		setCurrentArticle(art);
	}, [issue]);

	return {
		issue,
		currentArticle,
		setCurrentArticle,
		isLoading,
		error,
	};
}

/**
 * React hook that returns the details of the issue
 *
 * @param id - Props that gets the issue by it's id
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const [issue, setIssue] = useState<Issue | null>();
	const fetchPolicy = useFetchPolicy(
		`issue:${id}`,
		config?.app?.cache?.ttl?.getIssue,
	);
	const variables = { id, isSlug: !isNumeric(id) };
	const { loading, error, data } = useQuery<{ issue: Issue | null }>(
		GET_ISSUE,
		{
			fetchPolicy,
			variables,
		},
	);

	useIssueNotFound(issue);

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}

		if (!data) return;

		setIssue(clone(data.issue));
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

interface GetIssueResponse {
	isLoading: boolean;
	issue?: Issue | null;
	error?: Error | null;
}

/*export function mapArticlesById(ids: Set<string>): any {
	return (article: any) => {
		if (ids.has(`${article.id}`)) {
			if (article.name['de']) {
				delete article.name['de'];
			}
			if (article.name['fr']) {
				delete article.name['fr'];
			}
			article.components = article.components.reduce(removeLanguage, []);
			return article;
		}
		return article;
	};
}

export function removeLanguage(acc: Array<any>, component: any) {
	if (component.text_lang) {
		if (component.text_lang['de']) {
			delete component.text_lang['de'];
		}
		if (component.text_lang['fr']) {
			delete component.text_lang['fr'];
		}
		acc.push(component);
		return acc;
	}
	if (component.columns) {
		console.log(component);
		component.columns = component.columns.map((c: Array<any>) =>
			c.reduce(removeLanguage, []),
		);
		acc.push(component);
		return acc;
	}

	if (component.components) {
		component.components = component.components.reduce(removeLanguage, []);
		acc.push(component);
		return acc;
	}
	acc.push(component);
	return acc;
}*/

interface NavigationResponse extends GetIssueResponse {
	currentArticle: any | null | undefined;
	setCurrentArticle: (article: any) => void;
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(args: TitleArgs) {
	const { article, lang } = args;
	useEffect(() => {
		if (!article) {
			return;
		}
		if (!lang) {
			return;
		}

		const { name, description } = article;

		if (name[lang]) {
			document.title = name[lang];
		}

		if (description) {
			const metaHeader = document.querySelector(
				'meta[name="description"]',
			);
			if (metaHeader) {
				metaHeader.setAttribute('content', description);
			}
		}
	}, [article]);
}

interface TitleArgs {
	article: any | null | undefined;
	lang: string;
}

/**
 * React hook that control font
 *
 */
export function useFont(): FontResponse {
	const [fontSize, setFontSize] = useState(
		localStorage.getItem('font-size')
			? parseFloat(`${localStorage.getItem('font-size')}`)
			: 62.5,
	);
	useEffect(() => {
		localStorage.setItem('font-size', `${fontSize}`);
	}, [fontSize]);
	return {
		fontSize,
		setFontSize,
	};
}

interface FontResponse {
	fontSize: number;
	setFontSize: (size: number) => void;
}

/**
 * React hook that runs when an article change
 *
 */
export function useArticle({ issue, article }: ArticleArgs) {
	const navigate = useNavigate();
	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!article) {
			return;
		}

		let url = `/issues/${issue.slug || issue.id}/${
			article.slug || article.id
		}`;

		if (article.hash) {
			url += `#${article.hash}`;
		} else if (window.location.hash) {
			url += window.location.hash;
		}

		navigate(url, { replace: true });

		scrollTop(article.id);
	}, [issue, article]);
	useAutoPlayVideos({ article });
}

interface ArticleArgs {
	issue?: any | null | undefined;
	article: any | null;
}

function useAutoPlayVideos({ article }: ArticleArgs) {
	useEffect(() => {
		if (!article) {
			return;
		}
		const articleNode = document.getElementById(`article-${article.id}`);
		if (!articleNode) {
			return;
		}

		/*Check for video elements*/
		const videoNodes = articleNode.querySelectorAll('video');
		for (const node of videoNodes) {
			if (node.hasAttribute('autoplay')) {
				node.play();
			}
		}
	}, [article]);
}

async function getIssueById(args: any) {
	const { id, apolloClient, appKey, token, onlineStatus, db, kv } = args;
	/*const cacheIssue = await db.getIssue(id);
	console.log(`ISSUE FROM CACHE: `, cacheIssue);
	if (cacheIssue) {
		cacheIssue.articles = [];
		const articles = await db.getArticlesByIssue(cacheIssue.id);
		const nonFeatureArticles = articles.filter((a: Article) => a.isFeatured === false);
		if (nonFeatureArticles.length) {
			console.log(`CACHE ARTICLES: `, articles);
			cacheIssue.articles = articles;
			const isArticlesHaveComponents = articles
				.reduce(reduceCountComponents, 0) >= articles.length;
			if (!isArticlesHaveComponents) {
				console.log(`I only have the titles of the article`);
			}
			if (onlineStatus && sw) {
				console.log(`Download issue in the background`);
				sw.postMessage({
					type: 'DOWNLOAD_BACKGROUND_ISSUE',
					payload: {
						id: cacheIssue.id,
					},
				});
			}
			return clone(cacheIssue);
		}
	}*/

	if (onlineStatus) {
		const issue = await getRemoteIssueById({
			id,
			apolloClient,
			appKey,
			token,
			kv,
		});

		console.log(`I didn't find in the cache i went online`, issue);

		if (issue) {
			await saveIssueInCache({ issue, db });
		}

		return issue;
	}

	return await db.getIssue(id);
}

async function getRemoteIssueById(args: any) {
	const { id, apolloClient, appKey, token, kv } = args;
	const fetchPolicyKey = `issue-by-id-${id}`;

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getIssue ?? undefined,
		);
	}

	const result = await apolloClient.query({
		query: GET_ISSUE_BY_ID,
		fetchPolicy,
		headers: {
			'app-key': appKey,
			token,
			Authorization: getAccessToken(),
			'app-user-role': getUserRole(),
		},
		variables: {
			id,
		},
	});

	let { issue } = clone(result.data);
	if (issue && issue['__typename']) {
		delete issue['__typename'];
	}

	if (issue && issue.articles) {
		// TODO Remove this line to support feature
		issue = mapIssuesNonImplementedFeatures(issue);
		issue.articles = issue.articles
			.sort((a: any, b: any) => a.index - b.index)
			.map((article: any, index: number) => {
				article.index = index;
				if (article.components) {
					article.components = article.components.reduce(
						reduceExcludeComponents,
						[],
					);
				}

				return article;
			});
	}

	return issue;
}

async function saveIssueInCache(args: { issue: any; db: Db }) {
	const { issue, db } = args;
	await db.saveIssue(clone(issue));
}

async function getIssueBySlug(args: any) {
	const { id, apolloClient, appKey, token, onlineStatus, db, sw, kv } = args;
	const cacheIssue = await db.getIssue(id, true);

	const getRemoteData = async () => {
		try {
			const issue = await getRemoteIssueBySlug({
				id,
				apolloClient,
				appKey,
				token,
				kv,
			});
			console.log(`Load issue from REMOTE using slug`, issue);
			if (issue) {
				await saveIssueInCache({ issue, db });
			}
			return issue;
		} catch (e) {
			console.error(e);
		}

		return cacheIssue;
	};

	if (onlineStatus) {
		return await getRemoteData();
	}

	if (cacheIssue) {
		cacheIssue.articles = [];
		const articles = await db.getArticlesByIssue(cacheIssue.id);
		//	const nonFeatureArticles = articles.filter((a: Article) => a.isFeatured === false);
		cacheIssue.articles = articles;
		const isArticlesHaveComponents =
			articles.reduce(reduceCountComponents, 0) >= articles.length;
		if (isArticlesHaveComponents) {
			if (onlineStatus && sw) {
				console.info(
					`I send the request to service worker to fetch: `,
					cacheIssue.id,
				);
				sw.postMessage({
					type: 'DOWNLOAD_BACKGROUND_ISSUE',
					payload: {
						id: cacheIssue.id,
						token: getAccessToken(),
					},
				});
			}

			console.log(`Load issue from the cache using slug`, cacheIssue);
			if (articles.length) {
				return clone(cacheIssue);
			}
		}

		if (onlineStatus === false && articles.length) {
			return clone(cacheIssue);
		}
	} else {
		return await getRemoteData();
	}

	return cacheIssue;
}

function reduceCountComponents(acc: number, article: Article) {
	if (!article.components) {
		return acc;
	}

	acc += article.components.length;
	return acc;
}

async function getRemoteIssueBySlug(args: any) {
	const { id, apolloClient, appKey, token, kv } = args;

	const fetchPolicyKey = `issue-by-slug-${id}`;

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getIssue ?? undefined,
		);
	}

	const result = await apolloClient.query({
		query: GET_ISSUE_BY_SLUG,
		fetchPolicy,
		headers: {
			'app-key': appKey,
			token,
			Authorization: getAppToken(),
			'app-user-role': getUserRole(),
		},
		variables: {
			slug: id,
		},
	});

	let { issue } = clone(result.data);
	if (issue && issue['__typename']) {
		delete issue['__typename'];
	}

	if (issue && issue.articles) {
		// TODO Remove this line to support feature
		issue = mapIssuesNonImplementedFeatures(issue);
		issue.articles = issue.articles
			.sort((a: any, b: any) => a.index - b.index)
			.map((article: any, index: number) => {
				article.index = index;
				if (article.components) {
					article.components = article.components.reduce(
						reduceExcludeComponents,
						[],
					);
				}

				if (article.pages) {
					article.pages = article.pages.map((page: string) =>
						fixInvalidInPages(page),
					);
				}
				if (article?.replica?.image) {
					article.replica.image = fixInvalidInPages(
						article.replica.image,
					);
				}
				return article;
			});
	}

	return issue;
}

function fixInvalidInPages(page: string) {
	return page
		.replace(
			/https:\/\/cdn\.pwa\.canvasflow\.io\/https:\/\/cdn\.pwa\.canvasflow\.io/,
			'https://cdn.pwa.canvasflow.io',
		)
		.replace(
			/https:\/\/cdn\.pwa\.cflowdev\.com\/https:\/\/cdn\.pwa\.cflowdev\.com/,
			'https://cdn.pwa.cflowdev.com',
		);
}

/**
 * React hook that download images in the background
 *
 */
export function useDownloadAssets(args: DownloadImagesArgs) {
	const { issue, sw } = args;
	useEffect(() => {
		/*if (!issue || !issue.articles) {
			return;
		}*/
		if (!issue || !sw || !issue.articles) {
			return;
		}

		const images = [
			...issue.articles.reduce(reduceArticles, new Set<string>()),
		];

		/*const videos = [
			...issue.articles.reduce(reduceVideos, new Set<string>()),
		];*/

		sw.postMessage({
			type: 'DOWNLOAD_BACKGROUND_IMAGES',
			payload: {
				images,
				IssueID: issue.id,
			},
		});

		/*sw.postMessage({
			type: 'DOWNLOAD_BACKGROUND_VIDEOS',
			payload: {
				videos,
				IssueID: issue.id,
			},
		});*/
	}, [issue, sw]);
}

function reduceArticles(acc: Set<string>, article: any) {
	if (!article.components) {
		article.components = null;
		return acc;
	}
	return article.components.reduce(reduceComponents, acc);
}

function reduceComponents(acc: Set<string>, component: any) {
	const { components, imageurl } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}
	if (imageurl) {
		acc.add(imageurl);
	}
	return acc;
}

/*function reduceVideos(acc: Set<string>, article: any) {
	return article.components.reduce(reduceVideo, acc);
}

function reduceVideo(acc: Set<string>, component: any) {
	const { components, moviepath } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}

	if (moviepath) {
		acc.add(moviepath);
	}
	return acc;
}*/

interface DownloadImagesArgs {
	issue: any | null;
	sw: ServiceWorker | null;
}

export const GET_ISSUE = gql`
	query GetIssue($id: ID!, $isSlug: Boolean!) {
		issue(id: $id, isSlug: $isSlug) {
			id
			name
			description
			textDirection
			features
			pages
			cover
			externalUrl
			price
			currency
			slug
			data
			updated
			created
			isPurchased
			releaseDate
			articles {
				id
				ArticleID
				thumbnail
				role
				style
				name
				slug
				features
				keywords
				channel
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

export const GET_ISSUE_BY_ID = gql`
	${GET_ISSUE_BY_ID_QUERY}
`;

export const GET_ISSUE_BY_SLUG = gql`
	query Issue($slug: ID!) {
		issue(id: $slug, isSlug: true) {
			id
			name
			description
			textDirection
			pages
			cover
			externalUrl
			price
			currency
			slug
			data
			updated
			created
			isPurchased
			releaseDate
			articles {
				id
				ArticleID
				thumbnail
				role
				style
				name
				slug
				audio
				keywords
				channel
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

// TODO Revert to support features
export const HACK_GET_ISSUE_BY_SLUG = gql`
	query Issue($slug: ID!) {
		issue(id: $slug, isSlug: true) {
			id
			name
			description
			textDirection
			pages
			cover
			externalUrl
			price
			currency
			slug
			features
			data
			updated
			isFree
			created
			isPurchased
			releaseDate
			articles {
				id
				ArticleID
				thumbnail
				role
				style
				name
				slug
				audio
				keywords
				features
				channel
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

function scrollTop(articleId: string) {
	const el = document.getElementById(`article-${articleId}`);
	if (!el) {
		return;
	}

	const options: any = {
		top: 0,
		left: 0,
		behavior: 'instant',
	};

	el.scroll(options);
}
